import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import PremiumUpsell from "./PremiumUpsell";
import {usePremiumGate} from "../hooks/usePremiumGate";

interface MonthSpend {
    month: string;
    spent: number;
    items: number;
}

interface SpendHistory {
    months: number;
    totalSpentInPeriod: number;
    itemsInPeriod: number;
    averagePerMonth: number;
    spendByMonth: MonthSpend[];
}

interface Props {
    ownerId: string | null;
}

const money = (n: number | null | undefined): string =>
    n == null ? '—' : `${Math.round(n).toLocaleString('ru-RU')} ₽`;

/**
 * Самостоятельный блок истории трат на гардероб по месяцам (столбчатая диаграмма + сводка).
 * Грузит данные сам, чтобы не связываться с состоянием страницы стоимости.
 */
const SpendHistorySection: React.FC<Props> = ({ownerId}) => {
    const [data, setData] = useState<SpendHistory | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const {premiumGate, capturePremiumGate, resetPremiumGate} = usePremiumGate();

    const load = useCallback(async () => {
        setLoading(true);
        setError(false);
        resetPremiumGate();
        try {
            const api = await createAxiosInstance();
            const params = ownerId ? {ownerId} : {};
            const res = await api.get<SpendHistory>('/wardrobe-value/history', {params});
            setData(res.data);
        } catch (e) {
            // История трат — premium-аналитика: гейт объясняем панелью апселла, а не
            // ложным «Не удалось загрузить … Повторить» (R17).
            if (!capturePremiumGate(e, 'История трат доступна в Premium')) {
                setError(true);
            }
        } finally {
            setLoading(false);
        }
    }, [ownerId, capturePremiumGate, resetPremiumGate]);

    useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <section data-testid="spend-history-loading" className="animate-pulse">
                <div className="h-5 w-48 bg-gray-200 dark:bg-ink-700 rounded mb-3"/>
                <div className="h-32 bg-gray-100 dark:bg-ink-800 rounded-xl"/>
            </section>
        );
    }

    if (premiumGate) {
        return (
            <PremiumUpsell
                testId="spend-history-premium"
                title={premiumGate.title}
                description={premiumGate.description}
            />
        );
    }

    if (error || !data) {
        return (
            <section data-testid="spend-history-error" className="rounded-xl border border-gray-200 dark:border-ink-700 p-4">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Не удалось загрузить историю трат</p>
                <button
                    onClick={load}
                    className="px-3 py-1.5 rounded-xl bg-brand-600 text-white text-sm hover:bg-brand-700"
                >
                    Повторить
                </button>
            </section>
        );
    }

    if (data.itemsInPeriod === 0) {
        return (
            <section data-testid="spend-history-empty">
                <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">Траты по месяцам</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                    За последние {data.months} мес. покупок с датой не отмечено — указывайте дату покупки в карточках вещей.
                </p>
            </section>
        );
    }

    const maxSpent = Math.max(1, ...data.spendByMonth.map(m => m.spent));

    return (
        <section data-testid="spend-history" className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Траты по месяцам · {money(data.totalSpentInPeriod)} за {data.months} мес.
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="spend-history-avg">
                В среднем {money(data.averagePerMonth)} в месяц · {data.itemsInPeriod} покупок
            </p>

            <div
                className="flex items-end gap-2 h-32 rounded-xl border border-gray-200 dark:border-ink-700 p-3"
                data-testid="spend-history-chart"
                role="img"
                aria-label={`Траты на гардероб по месяцам за последние ${data.months} месяцев`}
            >
                {data.spendByMonth.map(m => (
                    <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full">
                        <span className="text-[10px] text-gray-500 dark:text-gray-400 mb-1">
                            {m.spent > 0 ? Math.round(m.spent / 1000) + 'к' : ''}
                        </span>
                        <div
                            className="w-full rounded-t bg-brand-500 dark:bg-brand-400 min-h-[2px]"
                            style={{height: `${(m.spent / maxSpent) * 100}%`}}
                            title={`${m.month}: ${money(m.spent)} (${m.items})`}
                        />
                        <span className="text-[10px] text-gray-400 mt-1">{m.month.slice(5)}</span>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default SpendHistorySection;
